import type { Item, Level, Vec2, Wall } from '../hp/types';
import { moveRoom, roomWalls, projectPoint, samePoint, MIN_WALL, type EndpointConnection, type VertexSnapTarget } from './edit';
import { boundsOf, itemFootprint, localFootprint, localToPlan, type Footprints, type LocalFootprint } from './footprint';
import { buildWallSolids } from './walls';
import { stackedItems, type SurfaceProbe } from './stacking';
import { openingKind } from '../../state/openingProperties';
import { isPlanItemVisible, type PlanSurface } from '../../state/planVisibility';
import { t } from '../../i18n/I18n';

export interface SnapGuide {
  from: Vec2;
  to: Vec2;
  kind: 'alignment' | 'equal-gap' | 'spacing' | 'wall' | 'vertex';
  length?: number;
}
export interface DistanceMarker { from: Vec2; to: Vec2; length: number }
export interface SnapCapture { axis: 'x' | 'z'; target: string; value: number }
export interface SnapOptions {
  /** Reads the loaded meshes for what stands on the dragged item, so a passenger on a seat is no snap target either. */
  probe?: SurfaceProbe;
  scale: number;
  disabled?: boolean;
  surface?: PlanSurface;
  footprints?: Footprints;
  previous?: SnapCapture[];
  axis?: 'x' | 'z';
  /** The press position lets an edit leave a magnet it already starts beside. */
  origin?: Vec2;
}
export interface ItemSnapResult { position: Vec2; guides: SnapGuide[]; captures: SnapCapture[] }
interface Candidate extends SnapCapture { delta: number; guides: SnapGuide[]; priority: number }
const EPS = 1e-6;
const add = (a: Vec2, b: Vec2): Vec2 => ({ x: a.x + b.x, z: a.z + b.z });
const sub = (a: Vec2, b: Vec2): Vec2 => ({ x: a.x - b.x, z: a.z - b.z });
const mul = (a: Vec2, factor: number): Vec2 => ({ x: a.x * factor, z: a.z * factor });
const dot = (a: Vec2, b: Vec2) => a.x * b.x + a.z * b.z;
const length = (a: Vec2) => Math.hypot(a.x, a.z);
/** Do not pull a deliberate move back toward an alignment already within reach at the press. */
const leavesInitialSnap = (distance: number, initial: number, tolerance: number) =>
  initial <= tolerance + EPS && distance > initial + EPS;
const axisPoint = (axis: 'x' | 'z', main: number, other: number): Vec2 => axis === 'x' ? { x: main, z: other } : { x: other, z: main };
const span = (bounds: LocalFootprint, axis: 'x' | 'z') => axis === 'x' ? [bounds.minX, bounds.maxX] : [bounds.minZ, bounds.maxZ];
const center = (bounds: LocalFootprint, axis: 'x' | 'z') => { const [lo, hi] = span(bounds, axis); return (lo + hi) / 2; };
const verticalOverlap = (a: Item, b: Item) => b.elevation < a.elevation + a.size.h - .001 && b.elevation + b.size.h > a.elevation + .02;

const stationary = new WeakMap<Item, { footprint?: LocalFootprint; quad: Vec2[]; bounds: LocalFootprint }>();
function stationaryGeometry(item: Item, footprints?: Footprints) {
  let value = stationary.get(item);
  const footprint = footprints?.get(item.id);
  if (!value || value.footprint !== footprint) {
    const quad = itemFootprint(item, footprints);
    value = { footprint, quad, bounds: boundsOf(quad) }; stationary.set(item, value);
  }
  return value;
}

/** Targets and guide geometry are resolved together, so a proximity is never drawn as a capture. */
export function smartSnapItem(level: Level, id: string, raw: Vec2, options: SnapOptions): ItemSnapResult {
  const original = level.items.find(item => item.id === id);
  if (!original || options.disabled) return { position: raw, guides: [], captures: [] };
  const active = { ...original, position: raw };
  const activeQuad = itemFootprint(active, options.footprints), activeBounds = boundsOf(activeQuad);
  const originQuad = options.origin ? itemFootprint({ ...active, position: options.origin }, options.footprints) : null;
  const tolerance = 10 * options.scale;
  const minNeighbor = Math.min(Math.min(activeBounds.maxX - activeBounds.minX, activeBounds.maxZ - activeBounds.minZ) * .35, .4);
  // Passengers travel with the item, so aligning it to them would just chase a fixed offset.
  const travelling = new Set(stackedItems(level.items, original, options.probe).map(item => item.id));
  const neighbors = level.items.filter(item => item.id !== id && !travelling.has(item.id)
    && isPlanItemVisible(item, options.surface ?? 'floor', level.height)
    && verticalOverlap(active, item)).map(item => ({ item, bounds: stationaryGeometry(item, options.footprints).bounds }))
    .filter(({ bounds }) => Math.min(bounds.maxX - bounds.minX, bounds.maxZ - bounds.minZ) >= minNeighbor)
    .sort((a, b) => a.item.id.localeCompare(b.item.id));
  const candidates: Candidate[] = [];
  for (const axis of ['x', 'z'] as const) {
    if (options.axis && axis !== options.axis) continue;
    const other = axis === 'x' ? 'z' : 'x';
    const [lo, hi] = span(activeBounds, axis), mid = (lo + hi) / 2;
    const aFeatures = [lo, mid, hi];
    for (const neighbor of neighbors) {
      const [nlo, nhi] = span(neighbor.bounds, axis), nFeatures = [nlo, (nlo + nhi) / 2, nhi];
      for (let a = 0; a < 3; a++) for (let n = 0; n < 3; n++) {
        const delta = nFeatures[n] - aFeatures[a];
        if (Math.abs(delta) > tolerance) continue;
        const [aOtherLo, aOtherHi] = span(activeBounds, other), [nOtherLo, nOtherHi] = span(neighbor.bounds, other);
        candidates.push({ axis, value: raw[axis] + delta, delta, target: `${neighbor.item.id}:${a}:${n}`,
          priority: a === 1 && n === 1 ? 1 : 2,
          guides: [{ kind: 'alignment', from: axisPoint(axis, nFeatures[n], Math.min(aOtherLo, nOtherLo)), to: axisPoint(axis, nFeatures[n], Math.max(aOtherHi, nOtherHi)) }] });
      }
    }
    const aligned = neighbors.filter(n => Math.abs(center(n.bounds, other) - center(activeBounds, other)) <= tolerance)
      .sort((a, b) => center(a.bounds, axis) - center(b.bounds, axis));
    const left = aligned.filter(n => center(n.bounds, axis) < mid).at(-1);
    const right = aligned.find(n => center(n.bounds, axis) > mid);
    const lane = center(activeBounds, other);
    if (left && right) {
      const leftEdge = span(left.bounds, axis)[1], rightEdge = span(right.bounds, axis)[0];
      const delta = (leftEdge + rightEdge) / 2 - mid;
      const gap = (rightEdge - leftEdge - (hi - lo)) / 2;
      if (Math.abs(delta) <= tolerance && gap > .01) candidates.push({ axis, value: raw[axis] + delta, delta,
        target: `gap:${left.item.id}:${right.item.id}`, priority: 0,
        guides: [{ kind: 'equal-gap', from: axisPoint(axis, leftEdge, lane), to: axisPoint(axis, lo + delta, lane), length: gap },
          { kind: 'equal-gap', from: axisPoint(axis, hi + delta, lane), to: axisPoint(axis, rightEdge, lane), length: gap }] });
    }
    if (aligned.length >= 2) {
      const step = center(aligned[1].bounds, axis) - center(aligned[0].bounds, axis);
      const regular = step > .01 && aligned.slice(1).every((n, i) => Math.abs(center(n.bounds, axis) - center(aligned[i].bounds, axis) - step) <= .001);
      if (regular) for (const side of [-1, 1]) {
        const end = side < 0 ? aligned[0] : aligned.at(-1)!;
        const destination = center(end.bounds, axis) + side * step;
        const delta = destination - mid;
        if (Math.abs(delta) > tolerance) continue;
        const nodes = [...aligned.map(n => center(n.bounds, axis)), destination].sort((a, b) => a - b);
        candidates.push({ axis, value: raw[axis] + delta, delta, target: `step:${end.item.id}:${side}`, priority: 0,
          guides: nodes.slice(1).map((node, i) => ({ kind: 'spacing', from: axisPoint(axis, nodes[i], lane), to: axisPoint(axis, node, lane), length: step })) });
      }
    }
  }
  const position = { ...raw }, guides: SnapGuide[] = [], captures: SnapCapture[] = [];
  const wallCandidates: Array<{ position: Vec2; distance: number; id: string; normal: Vec2; guide: SnapGuide }> = [];
  for (const wall of level.walls) {
    const edge = sub(wall.end, wall.start), wallLength = length(edge);
    if (wallLength < .2) continue;
    const direction = mul(edge, 1 / wallLength), normal = { x: -direction.z, z: direction.x };
    const side = dot(sub(raw, wall.start), normal) >= 0 ? 1 : -1;
    const n = mul(normal, side), projections = activeQuad.map(point => dot(sub(point, wall.start), n));
    const gap = Math.min(...projections) - wall.thickness / 2;
    if (originQuad) {
      const initialGap = Math.min(...originQuad.map(point => dot(sub(point, wall.start), n))) - wall.thickness / 2;
      if (leavesInitialSnap(Math.abs(gap), Math.abs(initialGap), tolerance)) continue;
    }
    const along = activeQuad.map(point => dot(sub(point, wall.start), direction));
    if (Math.max(...along) <= 0 || Math.min(...along) >= wallLength || Math.abs(gap) > tolerance) continue;
    let correction = mul(n, -gap);
    if (options.axis) {
      if (Math.abs(n[options.axis]) < EPS) continue;
      correction = axisPoint(options.axis, -gap / n[options.axis], 0);
    }
    if (length(correction) > tolerance) continue;
    const p = add(raw, correction), face = mul(n, wall.thickness / 2);
    wallCandidates.push({ position: p, distance: length(correction), id: wall.id, normal: n,
      guide: { kind: 'wall', from: add(wall.start, face), to: add(wall.end, face) } });
  }
  wallCandidates.sort((a, b) => a.distance - b.distance || a.id.localeCompare(b.id));
  const wall = wallCandidates[0];
  if (wall) { Object.assign(position, wall.position); guides.push(wall.guide); }
  for (const axis of ['x', 'z'] as const) {
    if (wall && Math.abs(wall.normal[axis]) > EPS) continue;
    const previous = options.previous?.find(capture => capture.axis === axis);
    // An alignment acquired after leaving the press position keeps its normal retention rule.
    const matching = candidates.filter(candidate => candidate.axis === axis && (!options.origin
      || candidate.target === previous?.target && Math.abs(previous.value - options.origin[axis]) > EPS
      || !leavesInitialSnap(Math.abs(candidate.delta), Math.abs(options.origin[axis] - candidate.value), tolerance)));
    matching.sort((a, b) => {
      const score = (candidate: Candidate) => Math.abs(candidate.delta) - (candidate.target === previous?.target ? Math.min(.005, options.scale) : 0);
      return score(a) - score(b) || a.priority - b.priority || a.target.localeCompare(b.target);
    });
    const winner = matching[0];
    if (!winner) continue;
    position[axis] = options.origin && Math.abs(winner.value - options.origin[axis]) < EPS ? options.origin[axis] : winner.value;
    guides.push(...winner.guides); captures.push({ axis, value: winner.value, target: winner.target });
  }
  return { position, guides, captures };
}

/** Four swept edge clearances include obstacles whose centre misses the centre ray. */
export function distanceMarkers(level: Level, item: Item, footprints?: Footprints, surface: PlanSurface = 'floor'): DistanceMarker[] {
  const segments: Array<[Vec2, Vec2]> = [];
  const append = (polygon: Vec2[]) => polygon.forEach((p, i) => segments.push([p, polygon[(i + 1) % polygon.length]]));
  for (const solid of buildWallSolids(level)) {
    const corners = solid.corners;
    append(solid.outline ?? [corners.leftStart, corners.leftEnd, corners.rightEnd, corners.rightStart]);
  }
  for (const other of level.items) if (other.id !== item.id && other.size.h >= .06 && verticalOverlap(item, other)
    && isPlanItemVisible(other, surface, level.height)) append(stationaryGeometry(other, footprints).quad);
  const b = localFootprint(item, footprints), cx = (b.minX + b.maxX) / 2, cz = (b.minZ + b.maxZ) / 2;
  const c = localToPlan(item, cx, cz), ax = { x: Math.cos(item.rotation), z: Math.sin(item.rotation) }, az = { x: -ax.z, z: ax.x };
  const result: DistanceMarker[] = [];
  for (const [direction, extent, halfSpan] of [[ax, (b.maxX - b.minX) / 2, (b.maxZ - b.minZ) / 2],
    [mul(ax, -1), (b.maxX - b.minX) / 2, (b.maxZ - b.minZ) / 2],
    [az, (b.maxZ - b.minZ) / 2, (b.maxX - b.minX) / 2], [mul(az, -1), (b.maxZ - b.minZ) / 2, (b.maxX - b.minX) / 2]] as const) {
    const origin = add(c, mul(direction, extent)), side = { x: -direction.z, z: direction.x };
    let best = 8, lateral = 0;
    for (const [a, z] of segments) {
      const ar = sub(a, origin), br = sub(z, origin), aSide = dot(ar, side), bSide = dot(br, side);
      let t0 = 0, t1 = 1;
      const ds = bSide - aSide;
      if (Math.abs(ds) < EPS) { if (Math.abs(aSide) > halfSpan + EPS) continue; }
      else {
        const lo = (-halfSpan - aSide) / ds, hi = (halfSpan - aSide) / ds;
        t0 = Math.max(0, Math.min(lo, hi)); t1 = Math.min(1, Math.max(lo, hi));
        if (t1 < t0) continue;
      }
      const aDist = dot(ar, direction), bDist = dot(br, direction);
      for (const t of [t0, t1]) {
        const distance = aDist + (bDist - aDist) * t;
        if (distance >= .01 && distance < best) { best = distance; lateral = aSide + ds * t; }
      }
    }
    if (best < 8) {
      const from = add(origin, mul(side, lateral));
      result.push({ from, to: add(from, mul(direction, best)), length: best });
    }
  }
  return result;
}

/** A distance marker lying along a wall face; `inward` points into the room that face belongs to. */
export interface WallDistanceMarker extends DistanceMarker { inward: Vec2 }

/**
 * The distance markers of a door, window or opening moved along its wall — the floor-plan pair of
 * Unity's `OpeningFurnitureController` markers. From each jamb along the wall's room-side face to the
 * nearest thing the opening would run into: another opening in that face, a wall crossing the face
 * (a partition ending on it, the neighbour at an inner corner), or the end of the face. Walls that
 * continue the face in a straight line on the same side count as one face, so a joint in a straight
 * wall is not an obstacle; furniture standing in front of the wall is not one either. The markers lie
 * on the face itself; the plan lifts them off the wall's outline along `inward`.
 */
export function openingDistanceMarkers(level: Level, openingId: string): WallDistanceMarker[] {
  const opening = level.openings.find(value => value.id === openingId);
  const wall = opening && level.walls.find(value => value.id === opening.wallId);
  if (!opening || !wall || wall.hidden || wall.length < EPS) return [];
  const solids = buildWallSolids(level), solidOf = new Map(solids.map(solid => [solid.wall.id, solid]));
  const d = wall.dir, n = wall.normalIn, half = wall.thickness / 2;
  const along = (point: Vec2) => dot(sub(point, wall.start), d);
  const touches = (a: Vec2, b: Vec2) => Math.hypot(a.x - b.x, a.z - b.z) < 1e-3;
  const jambs = [opening.offset - opening.width / 2, opening.offset + opening.width / 2];
  const result: WallDistanceMarker[] = [];
  for (const sign of [-1, 1] as const) {
    const chain: Wall[] = [wall];
    for (let current = wall, step = 0; step < 16; step++) {
      const joint = sign * (along(current.end) - along(current.start)) > 0 ? current.end : current.start;
      const next = level.walls.find(other => !other.hidden && !chain.includes(other)
        && (touches(other.start, joint) || touches(other.end, joint))
        && Math.abs(dot(other.dir, d)) > .9999 && dot(other.normalIn, n) > .9999
        && Math.abs(dot(sub(other.center, wall.start), n)) < 1e-3 && sign * (along(other.center) - along(joint)) > 0);
      if (!next) break;
      chain.push(next); current = next;
    }
    const last = chain[chain.length - 1], solid = solidOf.get(last.id);
    const faceCorners = solid ? (solid.interiorIsLeft ? [solid.corners.leftStart, solid.corners.leftEnd] : [solid.corners.rightStart, solid.corners.rightEnd]) : [last.start, last.end];
    const faceEnd = sign > 0 ? Math.max(...faceCorners.map(along)) : Math.min(...faceCorners.map(along));
    const start = jambs[sign > 0 ? 1 : 0];
    let best = sign * (faceEnd - start);
    for (const other of level.openings) {
      const host = other.id === opening.id ? undefined : chain.find(value => value.id === other.wallId);
      if (!host) continue;
      const centre = along(add(host.start, mul(host.dir, other.offset))), gap = sign * (centre - sign * other.width / 2 - start);
      if (gap > -1e-3 && sign * (centre - start) > 0) best = Math.min(best, Math.max(0, gap));
    }
    // A probe a centimetre into the room: whatever crosses it stands on the face.
    const origin = add(add(wall.start, mul(d, start)), mul(n, half + .01)), direction = mul(d, sign);
    for (const other of solids) {
      if (other.wall.hidden || chain.includes(other.wall)) continue;
      const outline = other.outline;
      if (insidePolygon(outline, origin)) { best = 0; break; }
      for (let i = 0; i < outline.length; i++) {
        const t = rayHitsSegment(origin, direction, outline[i], outline[(i + 1) % outline.length]);
        if (t !== null && t < best) best = t;
      }
    }
    if (best < .01) continue;
    const from = add(add(wall.start, mul(d, start)), mul(n, half));
    result.push({ from, to: add(from, mul(direction, best)), length: best, inward: n });
  }
  return result;
}

/** How far along `direction` (a unit vector) the ray from `origin` meets segment p–q, or null. */
function rayHitsSegment(origin: Vec2, direction: Vec2, p: Vec2, q: Vec2): number | null {
  const edge = sub(q, p), denominator = direction.x * edge.z - direction.z * edge.x;
  if (Math.abs(denominator) < EPS) return null;
  const w = sub(p, origin), t = (w.x * edge.z - w.z * edge.x) / denominator, u = (w.x * direction.z - w.z * direction.x) / denominator;
  return u >= -EPS && u <= 1 + EPS && t >= 0 ? t : null;
}

function insidePolygon(polygon: readonly Vec2[], point: Vec2): boolean {
  let inside = false;
  for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
    const a = polygon[j], b = polygon[i];
    if ((a.z > point.z) !== (b.z > point.z) && point.x < (b.x - a.x) * (point.z - a.z) / (b.z - a.z) + a.x) inside = !inside;
  }
  return inside;
}

export interface PointSnapOptions { scale: number; disabled?: boolean; anchor?: Vec2; exclude?: Vec2[]; excludeWalls?: string[]; segments?: boolean; axis?: 'x' | 'z'; previous?: PointSnapCapture | null;
  /** Existing geometry follows precise pointer movement; new construction retains its grid. */
  origin?: Vec2;
  /** The far ends of the two walls meeting at the moved corner: on their line the walls are straight again. */
  straight?: [Vec2, Vec2];
  /** The far end of a lone wall whose free end is moved; a wall being drawn uses its `anchor`. */
  square?: Vec2 }
/**
 * What the last frame caught. A capture holds until the pointer leaves a wider release zone
 * (Figma «Snap Persistence»), so a point never flickers between snapped and free while the
 * hand hovers at the edge of a radius, and the anchor is not torn between two candidates.
 */
export interface PointSnapCapture { target?: VertexSnapTarget; angle?: number; axes?: { x?: number; z?: number }; straight?: boolean; square?: boolean }
export interface PointSnapResult { point: Vec2; guides: SnapGuide[]; target: VertexSnapTarget | null; capture: PointSnapCapture }
/** Capture radii in CSS pixels — measured in the plan, so they follow the zoom — and the standard angle cone. */
export const POINT_SNAP = { vertex: 16, segment: 12, alignment: 10, angle: Math.PI / 30, release: 1.5 } as const;
const angleBetween = (a: number, b: number) => Math.atan2(Math.sin(a - b), Math.cos(a - b));

/**
 * Snap a free point the way the wall tools need it, in Figma's priority order: an existing
 * endpoint first, then the body of a wall, then a standard drawing angle from `anchor`, then
 * alignment with an endpoint along either axis. A geometric capture always wins over the angle.
 */
export function snapVertexPoint(raw: Vec2, level: Level, options: PointSnapOptions): PointSnapResult {
  if (options.disabled) return { point: raw, guides: [], target: null, capture: {} };
  const previous = options.previous ?? {}, held = previous.target, release = POINT_SNAP.release;
  const excluded = (point: Vec2) => options.exclude?.some(other => samePoint(point, other));
  const locked = options.axis === 'x' ? 'z' : options.axis === 'z' ? 'x' : null;
  const anchors = level.walls.flatMap(wall => (['start', 'end'] as const).map(end => ({ point: wall[end], wallId: wall.id, end })))
    .filter(anchor => !excluded(anchor.point) && (!locked || Math.abs(anchor.point[locked] - raw[locked]) < EPS));
  anchors.sort((a, b) => length(sub(a.point, raw)) - length(sub(b.point, raw)) || a.wallId.localeCompare(b.wallId) || a.end.localeCompare(b.end));
  const heldVertex = held?.kind === 'vertex' ? anchors.find(anchor => anchor.wallId === held.wallId && anchor.end === held.end) : undefined;
  const vertex = heldVertex && length(sub(heldVertex.point, raw)) <= options.scale * POINT_SNAP.vertex * release ? heldVertex
    : anchors[0] && length(sub(anchors[0].point, raw)) <= options.scale * POINT_SNAP.vertex ? anchors[0] : undefined;
  if (vertex) {
    const target: VertexSnapTarget = { kind: 'vertex', ...vertex };
    return { point: vertex.point, target, guides: [{ from: vertex.point, to: vertex.point, kind: 'vertex' }], capture: { target } };
  }
  if (options.segments !== false) {
    const segments = level.walls.filter(wall => !options.excludeWalls?.includes(wall.id))
      .map(wall => {
        let hit = projectPoint(raw, wall.start, wall.end);
        if (locked) {
          const extent = wall.end[locked] - wall.start[locked];
          if (Math.abs(extent) > EPS) {
            const t = (raw[locked] - wall.start[locked]) / extent;
            const point = add(wall.start, mul(sub(wall.end, wall.start), t));
            hit = { point, t, distance: length(sub(point, raw)) };
          } else if (Math.abs(wall.start[locked] - raw[locked]) > EPS) hit.distance = Infinity;
        }
        return { ...hit, wallId: wall.id };
      })
      .filter(hit => hit.t > .001 && hit.t < .999)
      .sort((a, b) => a.distance - b.distance || a.wallId.localeCompare(b.wallId));
    const heldSegment = held?.kind === 'wall' ? segments.find(hit => hit.wallId === held.wallId) : undefined;
    const segment = heldSegment && heldSegment.distance <= options.scale * POINT_SNAP.segment * release ? heldSegment
      : segments[0] && segments[0].distance <= options.scale * POINT_SNAP.segment ? segments[0] : undefined;
    if (segment) {
      // An end landing on another wall's body is taken square to it near that spot: the pointer alone
      // never quite finds the right angle, and the wall would lean by however far the hand strayed.
      const from = locked ? undefined : options.square ?? options.anchor;
      const host = level.walls.find(wall => wall.id === segment.wallId);
      const foot = from && host ? projectPoint(from, host.start, host.end) : undefined;
      const square = !!foot && foot.t > .001 && foot.t < .999 && foot.distance >= MIN_WALL
        && length(sub(foot.point, segment.point)) <= options.scale * POINT_SNAP.alignment * (previous.square ? release : 1);
      const hit = square && foot ? foot : segment;
      const target: VertexSnapTarget = { kind: 'wall', wallId: segment.wallId, point: hit.point, t: hit.t };
      return { point: hit.point, target, guides: [{ from: hit.point, to: hit.point, kind: 'vertex' }], capture: square ? { target, square } : { target } };
    }
  }
  if (options.straight && !locked) {
    // A corner pulled out of a wall snaps back into line with its neighbours' far ends — the wall is
    // straight again — and stays there through the release band like any other capture.
    const [a, b] = options.straight, hit = projectPoint(raw, a, b), span = length(sub(b, a));
    const kept = !!previous.straight && hit.distance < POINT_SNAP.alignment * release * options.scale;
    const initialDistance = options.origin ? projectPoint(options.origin, a, b).distance : 0;
    const leaving = options.origin && !(kept && initialDistance > EPS)
      && leavesInitialSnap(hit.distance, initialDistance, POINT_SNAP.alignment * options.scale);
    if (!leaving && span > MIN_WALL * 2 && (kept || hit.distance < POINT_SNAP.alignment * options.scale)) {
      const t = Math.min(1 - MIN_WALL / span, Math.max(MIN_WALL / span, hit.t));
      return { point: add(a, mul(sub(b, a), t)), target: null, guides: [{ from: a, to: b, kind: 'wall' }], capture: { straight: true } };
    }
  }
  let point = options.origin ? { ...raw } : { x: Math.round(raw.x * 10) / 10, z: Math.round(raw.z * 10) / 10 };
  if (locked) point[locked] = raw[locked];
  const capture: PointSnapCapture = {};
  if (options.anchor && !locked) {
    const delta = sub(raw, options.anchor), angle = Math.atan2(delta.z, delta.x), nearest = Math.round(angle / (Math.PI / 4)) * Math.PI / 4;
    const kept = previous.angle !== undefined && Math.abs(angleBetween(angle, previous.angle)) < POINT_SNAP.angle * release ? previous.angle : undefined;
    const snapped = kept ?? (Math.abs(angle - nearest) < POINT_SNAP.angle ? nearest : undefined);
    if (snapped !== undefined) {
      point = add(options.anchor, mul({ x: Math.cos(snapped), z: Math.sin(snapped) }, Math.round(length(delta) * 10) / 10));
      capture.angle = snapped;
    }
  }
  const guides: SnapGuide[] = [];
  for (const axis of ['x', 'z'] as const) {
    if (locked === axis) continue;
    const keptValue = previous.axes?.[axis];
    const byAxis = anchors.filter(anchor => !options.origin
      || keptValue !== undefined && Math.abs(anchor.point[axis] - keptValue) < EPS && Math.abs(keptValue - options.origin[axis]) > EPS
      || !leavesInitialSnap(Math.abs(anchor.point[axis] - raw[axis]), Math.abs(anchor.point[axis] - options.origin[axis]), POINT_SNAP.alignment * options.scale))
      .sort((a, b) => Math.abs(a.point[axis] - raw[axis]) - Math.abs(b.point[axis] - raw[axis]) || a.wallId.localeCompare(b.wallId));
    const kept = keptValue !== undefined && Math.abs(keptValue - raw[axis]) < POINT_SNAP.alignment * release * options.scale
      ? byAxis.find(anchor => Math.abs(anchor.point[axis] - keptValue) < EPS) : undefined;
    const nearest = kept ?? (byAxis[0] && Math.abs(byAxis[0].point[axis] - raw[axis]) < POINT_SNAP.alignment * options.scale ? byAxis[0] : undefined);
    if (!nearest) continue;
    point[axis] = nearest.point[axis];
    guides.push({ from: nearest.point, to: point, kind: 'alignment' });
    (capture.axes ??= {})[axis] = nearest.point[axis];
  }
  return { point, guides, target: null, capture };
}

export interface WallSnapOptions { scale: number; disabled?: boolean; axis?: 'x' | 'z'; previous?: string[]; segment?: Wall }

/** Room walls resize the contour along a stable direction, as in Unity's CentralWallDragStrategy. */
function roomWallDirection(level: Level, wall: Wall): Vec2 | undefined {
  const contour = level.rooms.some(room => [wall.start, wall.center, wall.end].every(point =>
    room.polygon.some((a, i) => projectPoint(point, a, room.polygon[(i + 1) % room.polygon.length]).distance < EPS)));
  if (!contour) return;
  const edge = sub(wall.end, wall.start), span = length(edge);
  if (span < EPS) return;
  let direction = { x: -edge.z / span, z: edge.x / span };
  // Prefer an axis-aligned neighbour when a sloped contour can extend along it. Do not round
  // rotated rooms to world axes: their true normal preserves the existing right angles.
  for (const neighbor of level.walls) {
    if (neighbor.id === wall.id || ![neighbor.start, neighbor.end].some(p => samePoint(p, wall.start) || samePoint(p, wall.end))) continue;
    const vector = sub(neighbor.end, neighbor.start), size = length(vector);
    if (size < EPS) continue;
    const candidate = mul(vector, 1 / size);
    if (Math.min(Math.abs(candidate.x), Math.abs(candidate.z)) < EPS
      && Math.min(Math.abs(direction.x), Math.abs(direction.z)) > EPS) direction = candidate;
  }
  return direction;
}

export function snapWallTranslation(level: Level, id: string, raw: Vec2, options: WallSnapOptions):
  { delta: Vec2; guides: SnapGuide[]; connections: EndpointConnection[]; captures: string[] } {
  const wall = options.segment ?? level.walls.find(wall => wall.id === id);
  if (!wall || options.disabled) return { delta: raw, guides: [], connections: [], captures: [] };
  const direction = options.axis ? axisPoint(options.axis, 1, 0) : roomWallDirection(level, wall);
  const movement = direction ? mul(direction, dot(raw, direction)) : raw;
  const exclude = [wall.start, wall.end];
  const excludeWalls = level.walls.filter(w => w.id === id || exclude.some(p => projectPoint(p, w.start, w.end).distance < EPS)).map(w => w.id);
  type Candidate = { key: string; delta: Vec2; distance: number; priority: number; end: 'start' | 'end'; anchor: Vec2; axis?: 'x' | 'z' };
  const candidates: Candidate[] = [];
  const previous = options.previous ?? [];
  const offer = (key: string, delta: Vec2, radius: number, priority: number, end: 'start' | 'end', anchor: Vec2, axis?: 'x' | 'z') => {
    const distance = length(sub(delta, movement)), held = previous.includes(key);
    if (direction && Math.abs(delta.x * direction.z - delta.z * direction.x) > EPS) return;
    if (distance > radius * options.scale * (held ? POINT_SNAP.release : 1)) return;
    // A resize must respond to a small pull even when it starts next to another alignment.
    if (axis && !held && leavesInitialSnap(Math.abs(anchor[axis] - wall[end][axis] - movement[axis]),
      Math.abs(anchor[axis] - wall[end][axis]), radius * options.scale)) return;
    candidates.push({ key, delta, distance, priority, end, anchor, axis });
  };
  for (const end of ['start', 'end'] as const) {
    const point = add(wall[end], movement);
    for (const host of level.walls) {
      if (host.hidden) continue;
      for (const hostEnd of ['start', 'end'] as const) {
        const anchor = host[hostEnd];
        if (exclude.some(p => samePoint(p, anchor))) continue;
        const key = `${end}:${host.id}:${hostEnd}`;
        offer(`${key}:vertex`, sub(anchor, wall[end]), POINT_SNAP.vertex, 0, end, anchor);
        for (const axis of ['x', 'z'] as const) {
          if (direction && Math.abs(direction[axis]) < EPS) continue;
          const correction = anchor[axis] - point[axis];
          const delta = add(movement, direction ? mul(direction, correction / direction[axis]) : axisPoint(axis, correction, 0));
          offer(`${key}:${axis}`, delta, POINT_SNAP.alignment, 2, end, anchor, axis);
        }
      }
      if (excludeWalls.includes(host.id)) continue;
      let hit = projectPoint(point, host.start, host.end);
      if (direction) {
        const edge = sub(host.end, host.start), cross = direction.x * edge.z - direction.z * edge.x;
        if (Math.abs(cross) > EPS) {
          const offset = sub(host.start, point), t = (offset.x * edge.z - offset.z * edge.x) / cross;
          const intersection = add(point, mul(direction, t));
          hit = { ...projectPoint(intersection, host.start, host.end), point: intersection };
          if (hit.distance > EPS) continue;
        } else if (hit.distance > EPS) continue;
      }
      if (hit.t > .001 && hit.t < .999) offer(`${end}:${host.id}:wall`, sub(hit.point, wall[end]), POINT_SNAP.segment, 1, end, hit.point);
    }
  }
  const ranked = (values: Candidate[]) => values.sort((a, b) => a.priority - b.priority
    || Number(previous.includes(b.key)) - Number(previous.includes(a.key)) || a.distance - b.distance || a.key.localeCompare(b.key));
  const geometric = ranked(candidates.filter(c => !c.axis))[0];
  const chosen = geometric ? [geometric] : direction ? ranked(candidates.filter(c => c.axis)).slice(0, 1)
    : (['x', 'z'] as const).flatMap(axis => ranked(candidates.filter(c => c.axis === axis)).slice(0, 1));
  const delta = { ...movement };
  for (const candidate of chosen) {
    if (candidate.axis && !direction) delta[candidate.axis] = candidate.delta[candidate.axis];
    else Object.assign(delta, candidate.delta);
  }
  const guides: SnapGuide[] = chosen.map(c => c.axis ? { kind: 'alignment', from: c.anchor, to: add(wall[c.end], delta) }
    : { kind: 'vertex', from: c.anchor, to: c.anchor });
  if (direction && length(delta) > EPS) {
    for (const end of ['start', 'end'] as const) guides.push({ kind: 'alignment', from: wall[end], to: add(wall[end], delta) });
  }
  const connections: EndpointConnection[] = [];
  for (const end of ['start', 'end'] as const) {
    const p = add(wall[end], delta);
    const result = snapVertexPoint(p, level, { scale: .00001, exclude, excludeWalls });
    if (result.target) connections.push({ end, target: result.target });
  }
  return { delta, guides, connections, captures: chosen.map(c => c.key) };
}

export function distanceLabel(distance: number): string {
  return distance >= .995 ? t('#ValueMeters', distance.toFixed(2)) : t('#ValueCentimeters', Math.round(distance * 100));
}

export function snapRoomTranslation(level: Level, index: number, raw: Vec2, options: SnapOptions): { delta: Vec2; guides: SnapGuide[] } {
  if (options.disabled) return { delta: raw, guides: [] };
  const moving = roomWalls(level, index), owned = new Set(moving.map(w => w.id));
  const shared = new Set(level.rooms.filter(r => r.index !== index).flatMap(r => roomWalls(level, r.index).map(w => w.id)));
  const hosts = level.walls.filter(w => !owned.has(w.id) || shared.has(w.id));
  const tolerance = options.scale * 12;
  const candidates: Array<{ delta: Vec2; priority: number; distance: number; id: string; guides: SnapGuide[] }> = [];
  for (const wall of moving) for (const host of hosts) {
    const direction = mul(sub(host.end, host.start), 1 / host.length), normal = { x: -direction.z, z: direction.x };
    if (Math.abs(dot(mul(sub(wall.end, wall.start), 1 / wall.length), normal)) > .001) continue;
    const start = add(wall.start, raw), end = add(wall.end, raw);
    const gap = dot(sub(start, host.start), normal);
    const lo = Math.min(dot(sub(start, host.start), direction), dot(sub(end, host.start), direction));
    const hi = Math.max(dot(sub(start, host.start), direction), dot(sub(end, host.start), direction));
    if (Math.abs(gap) > tolerance || Math.min(hi, host.length) - Math.max(0, lo) < .2) continue;
    let correction = mul(normal, -gap);
    if (options.axis) {
      if (Math.abs(normal[options.axis]) < EPS) continue;
      correction = axisPoint(options.axis, -gap / normal[options.axis], 0);
    }
    const delta = add(raw, correction);
    const addCandidate = (next: Vec2, priority: number, id: string, marker?: Vec2, reach = tolerance * Math.SQRT2) => {
      if (options.axis && Math.abs(next[options.axis === 'x' ? 'z' : 'x'] - raw[options.axis === 'x' ? 'z' : 'x']) > EPS) return;
      const distance = length(sub(next, raw));
      if (distance > reach) return;
      candidates.push({ delta: { x: Math.round(next.x * 1e4) / 1e4, z: Math.round(next.z * 1e4) / 1e4 }, priority, distance, id,
        guides: [{ kind: 'wall', from: host.start, to: host.end }, ...(marker ? [{ kind: 'vertex' as const, from: marker, to: marker }] : [])] });
    };
    addCandidate(delta, 3, `${wall.id}:${host.id}`);
    // Stopping short of a host corner by less than the minimum wall length leaves a stub too short
    // to build, so the corner magnet always reaches that far however far the plan is zoomed in.
    // Without it the only outcomes in that band are a refused join and a room that will not attach.
    const cornerReach = Math.max(tolerance, MIN_WALL);
    for (const a of [wall.start, wall.end]) for (const b of [host.start, host.end]) {
      const along = dot(sub(b, add(a, delta)), direction);
      if (Math.abs(along) <= cornerReach) addCandidate(add(delta, mul(direction, along)), 1, `${wall.id}:${host.id}:corner`, b, cornerReach * Math.SQRT2);
    }
    for (const a of level.openings.filter(o => o.wallId === wall.id && !shared.has(wall.id)))
      for (const b of level.openings.filter(o => o.wallId === host.id)) {
        if (openingKind(a) !== openingKind(b) || Math.abs(a.width - b.width) > .001 || Math.abs(a.height - b.height) > .001 || Math.abs(a.sill - b.sill) > .001) continue;
        const source = add(wall.start, mul(sub(wall.end, wall.start), a.offset / wall.length));
        const target = add(host.start, mul(direction, b.offset));
        const along = dot(sub(target, add(source, delta)), direction);
        if (Math.abs(along) <= tolerance) addCandidate(add(delta, mul(direction, along)), openingKind(a) === 'window' ? 2 : 0, `${a.id}:${b.id}`, target);
      }
  }
  candidates.sort((a, b) => a.priority - b.priority || a.distance - b.distance || a.id.localeCompare(b.id));
  const seen = new Set<string>();
  for (const candidate of candidates) {
    const key = `${candidate.delta.x},${candidate.delta.z}`;
    if (seen.has(key)) continue; seen.add(key);
    if (moveRoom(level, index, candidate.delta).level) return { delta: candidate.delta, guides: candidate.guides };
  }
  return { delta: raw, guides: [] };
}

export interface OpeningSnapResult { offset: number; guides: SnapGuide[] }

/**
 * Where a door or window on a wall wants to sit: centred on the wall, centred between its two
 * neighbours (the openings beside it, or the wall's ends), or one more step in an even row. The
 * nearest such place within the capture radius takes the opening; the guides show the equal
 * distances it was chosen for, laid out on the room side of the wall.
 */
export function snapOpeningOffset(level: Level, wallId: string, width: number, raw: number,
  options: { scale: number; disabled?: boolean; exclude?: string }): OpeningSnapResult {
  const wall = level.walls.find(w => w.id === wallId);
  if (!wall || options.disabled) return { offset: raw, guides: [] };
  const half = width / 2, margin = .02, tolerance = 10 * options.scale;
  const lane = wall.thickness / 2 + 20 * options.scale;
  const at = (offset: number): Vec2 => add(add(wall.start, mul(wall.dir, offset)), mul(wall.normalIn, lane));
  const gap = (from: number, to: number, value: number): SnapGuide => ({ kind: 'equal-gap', from: at(from), to: at(to), length: value });
  // Edges along the wall, the wall's own ends included as zero-width neighbours.
  const edges = [{ left: 0, right: 0 }, ...level.openings.filter(o => o.wallId === wallId && o.id !== options.exclude)
    .map(o => ({ left: o.offset - o.width / 2, right: o.offset + o.width / 2 })).sort((a, b) => a.left - b.left), { left: wall.length, right: wall.length }];
  const fits = (centre: number) => edges.every(edge => edge.right <= centre - half + 1e-9 || edge.left >= centre + half - 1e-9)
    && centre >= half + margin - 1e-9 && centre <= wall.length - half - margin + 1e-9;
  const candidates: Array<{ centre: number; guides: SnapGuide[]; rank: number }> = [];
  const left = edges.filter(edge => edge.right <= raw - half + tolerance), right = edges.filter(edge => edge.left >= raw + half - tolerance);
  const prev = left[left.length - 1], next = right[0];
  if (prev && next) {
    const centre = (prev.right + next.left) / 2, side = centre - half - prev.right;
    candidates.push({ centre, rank: 0, guides: [gap(prev.right, centre - half, side), gap(centre + half, next.left, side)] });
  }
  const middle = wall.length / 2;
  candidates.push({ centre: middle, rank: 1, guides: [gap(0, middle - half, middle - half), gap(middle + half, wall.length, middle - half)] });
  const before = left[left.length - 2], after = right[1];
  if (prev && before) {
    const step = prev.left - before.right, centre = prev.right + step + half;
    candidates.push({ centre, rank: 2, guides: [gap(before.right, prev.left, step), gap(prev.right, centre - half, step)] });
  }
  if (next && after) {
    const step = after.left - next.right, centre = next.left - step - half;
    candidates.push({ centre, rank: 2, guides: [gap(centre + half, next.left, step), gap(next.right, after.left, step)] });
  }
  const best = candidates.filter(c => Number.isFinite(c.centre) && Math.abs(c.centre - raw) <= tolerance && fits(c.centre))
    .sort((a, b) => Math.abs(a.centre - raw) - Math.abs(b.centre - raw) || a.rank - b.rank)[0];
  return best ? { offset: Math.round(best.centre * 1e4) / 1e4, guides: best.guides } : { offset: raw, guides: [] };
}
